import { KnowledgeApi } from "@/lib/api/knowledge";
import { useState } from "react";
import { Loader } from "@/components/ui/loader";
import { KnowledgeTaskCard } from "./components/KnowledgeTaskCard";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronRight } from "lucide-react";

interface StarredKnowledgeItemsViewProps {
  tasks: KnowledgeApi.KnowledgeItem[];
  resources: KnowledgeApi.KnowledgeItem[];
  userId?: string;
  tasksLoading: boolean;
  resourcesLoading: boolean;
  tasksPaginationControls: React.ReactNode;
  resourcesPaginationControls: React.ReactNode;
}

export function StarredKnowledgeItemsView({
  tasks,
  resources,
  userId,
  tasksLoading,
  resourcesLoading,
  tasksPaginationControls,
  resourcesPaginationControls,
}: StarredKnowledgeItemsViewProps) {
  const [tasksOpen, setTasksOpen] = useState(true);
  const [resourcesOpen, setResourcesOpen] = useState(true);

  return (
    <div className="space-y-6">
      <Collapsible open={tasksOpen} onOpenChange={setTasksOpen}>
        <CollapsibleTrigger className="flex items-center gap-2 text-sm font-semibold w-full text-left">
          {tasksOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          Starred Tasks
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-4 space-y-4">
          {tasksLoading ? (
            <Loader />
          ) : tasks.length > 0 ? (
            <>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {tasks.map((task) => (
                  <KnowledgeTaskCard
                    key={`starred-task-${task.id}`}
                    task={task}
                    isAssigned={task.assignments?.some((a) => a.assignee_id === userId) ?? false}
                    isStarred={task.is_starred ?? false}
                    assignmentCount={task.assignmentsCount ?? 0}
                  />
                ))}
              </div>
              {tasksPaginationControls}
            </>
          ) : (
            <div className="border rounded-lg p-6 bg-background text-center">
              <h3 className="font-medium mb-2">No starred tasks</h3>
              <p className="text-sm text-muted-foreground">You haven't starred any tasks yet.</p>
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>

      <Collapsible open={resourcesOpen} onOpenChange={setResourcesOpen}>
        <CollapsibleTrigger className="flex items-center gap-2 text-sm font-semibold w-full text-left">
          {resourcesOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          Starred Knowledge Resources
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-4 space-y-4">
          {resourcesLoading ? (
            <Loader />
          ) : resources.length > 0 ? (
            <>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {resources.map((resource) => (
                  <KnowledgeTaskCard
                    key={`starred-resource-${resource.id}`}
                    task={resource}
                    isAssigned={false}
                    isStarred={resource.is_starred ?? false}
                    assignmentCount={resource.assignmentsCount ?? 0}
                  />
                ))}
              </div>
              {resourcesPaginationControls}
            </>
          ) : (
            <div className="border rounded-lg p-6 bg-background text-center">
              <h3 className="font-medium mb-2">No starred resources</h3>
              <p className="text-sm text-muted-foreground">You haven't starred any knowledge resources yet.</p>
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
